import {useEffect, useState} from 'react';

const LOG_PREFIX = '[useEntityLinks]';
const BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {Authorization: `Bearer ${token}`} : {};
}

/**
 * ICRs targeting this entity (In Products) and other charts containing it.
 *
 * GET /api/entities/{id}/links?excludeChartId=
 *
 * @param {string|null} entityId
 * @param {string|null} [excludeChartId]
 * @param {number} [refetchKey=0]
 * @param {boolean} [enabled=true]
 */
export function useEntityLinks(entityId, excludeChartId = null, refetchKey = 0, enabled = true) {
    const [entityIdOut, setEntityIdOut] = useState(null);
    const [icrs, setIcrs] = useState([]);
    const [charts, setCharts] = useState([]);
    const [loading, setLoading] = useState(!!entityId);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled || !entityId) {
            if (!entityId) {
                setEntityIdOut(null);
                setIcrs([]);
                setCharts([]);
            }
            setLoading(false);
            setError(null);
            return undefined;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (excludeChartId) {
            params.set('excludeChartId', excludeChartId);
        }
        const qs = params.toString();
        const url = `${BASE}/api/entities/${encodeURIComponent(entityId)}/links${qs ? `?${qs}` : ''}`;

        fetch(url, {headers: authHeaders(), signal: controller.signal})
            .then(async (res) => {
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    throw new Error(body || `Failed to load entity links (${res.status})`);
                }
                return res.json();
            })
            .then((data) => {
                setEntityIdOut(data?.entityId ?? entityId);
                setIcrs(Array.isArray(data?.icrs) ? data.icrs : []);
                setCharts(Array.isArray(data?.charts) ? data.charts : []);
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                console.warn(LOG_PREFIX, err);
                setError(err);
                setIcrs([]);
                setCharts([]);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [entityId, excludeChartId, refetchKey, enabled]);

    const totalCount = icrs.length + charts.length;

    return {
        entityId: entityIdOut,
        icrs,
        charts,
        totalCount,
        loading,
        error,
    };
}

export default useEntityLinks;
